import type { Thunk, Action } from 'easy-peasy';
import { action, thunk } from 'easy-peasy';
import type { Injections } from '../../services/injections';
import type { ApplicationErrorOptions } from '../../../types';
import parseStatusCode from '../../errors/parseStatusCode';
import type { PCOrigin, PCOriginCreate, PCOriginScheduleEntry, PCOriginScheduleEntryCreate } from '../types';

export interface PCOriginsModel {
  pcOrigins: Record<string, PCOrigin>;
  pcSchedules: Record<string, Record<string, PCOriginScheduleEntry>>;
  fetchPCOrigins: Thunk<PCOriginsModel, void, Injections, Promise<PCOrigin[]>>;
  fetchPCOrigin: Thunk<PCOriginsModel, string, Injections, Promise<PCOrigin>>;
  createPCOrigin: Thunk<PCOriginsModel, PCOriginCreate, Injections, Promise<PCOrigin>>;
  deletePCOrigin: Thunk<PCOriginsModel, string, Injections, Promise<PCOrigin>>;
  pcOriginFailure: Action<PCOriginsModel, ApplicationErrorOptions>;
  updatePCOrigin: Thunk<PCOriginsModel, PCOrigin, Injections, Promise<PCOrigin>>;
  setPCOrigins: Action<PCOriginsModel, PCOrigin[]>;
  setPCOrigin: Action<PCOriginsModel, PCOrigin>;
  removePCOrigin: Action<PCOriginsModel, string>;
  setPCSchedules: Action<PCOriginsModel, { originId: string; schedules: PCOriginScheduleEntry[] }>;
  setPCSchedule: Action<PCOriginsModel, { originId: string; schedule: PCOriginScheduleEntry }>;
  removePCSchedule: Action<PCOriginsModel, { originId: string; scheduleId: string }>;
  fetchPCSchedules: Thunk<PCOriginsModel, string, Injections, Promise<PCOriginScheduleEntry[]>>;
  createPCSchedule: Thunk<
    PCOriginsModel,
    { originId: string; schedule: PCOriginScheduleEntryCreate },
    Injections,
    Promise<PCOriginScheduleEntry>
  >;
  deletePCSchedule: Thunk<
    PCOriginsModel,
    { originId: string; scheduleId: string },
    Injections,
    Promise<PCOriginScheduleEntry>
  >;
}

export const originsModel: PCOriginsModel = {
  pcOrigins: {},
  pcSchedules: {},
  pcOriginFailure: action((state, error) => {
    console.log(error);
  }),
  setPCOrigins: action((state, payload) => {
    state.pcOrigins = {};
    for (const val of payload) {
      state.pcOrigins[val.originId] = val;
    }
  }),
  setPCOrigin: action((state, payload) => {
    state.pcOrigins = { ...state.pcOrigins, [payload.originId]: payload };
  }),
  removePCOrigin: action((state, payload) => {
    const updatedOrigin = { ...state.pcOrigins };
    delete updatedOrigin[payload];
    state.pcOrigins = updatedOrigin;
  }),
  fetchPCOrigins: thunk(async (actions, payload, { injections }) => {
    try {
      const origins = await injections.partnerConfigurationService.fetchPCOrigins();
      actions.setPCOrigins(origins.results);
      return origins.results;
    } catch (error) {
      actions.pcOriginFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured fetching origins',
        asNotification: true,
      });
    }
  }),
  fetchPCOrigin: thunk(async (actions, payload, { injections }) => {
    try {
      const origin = await injections.partnerConfigurationService.fetchPCOrigin(payload);
      actions.setPCOrigin(origin);
      return origin;
    } catch (error) {
      actions.pcOriginFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured fetching origin',
        asNotification: true,
      });
    }
  }),
  createPCOrigin: thunk(async (actions, payload, { injections }) => {
    try {
      const origin = await injections.partnerConfigurationService.createPCOrigin(payload);
      actions.setPCOrigin(origin);
      return origin;
    } catch (error) {
      actions.pcOriginFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured creating origin.',
        asNotification: true,
      });
    }
  }),
  updatePCOrigin: thunk(async (actions, payload, { injections }) => {
    try {
      const origin = await injections.partnerConfigurationService.updatePCOrigin(payload);
      actions.setPCOrigin(origin);
      return origin;
    } catch (error) {
      actions.pcOriginFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured updating origin.',
        asNotification: true,
      });
    }
  }),
  deletePCOrigin: thunk(async (actions, payload, { injections }) => {
    try {
      const origin = await injections.partnerConfigurationService.deletePCOrigin(payload);
      actions.removePCOrigin(origin.originId);
      return payload;
    } catch (error) {
      actions.pcOriginFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured deleting origin.',
        asNotification: true,
      });
    }
  }),
  setPCSchedules: action((state, payload) => {
    const scheduleMap: Record<string, (typeof payload.schedules)[number]> = {};
    for (const val of payload.schedules) {
      scheduleMap[val.scheduleEntryId] = val;
    }
    state.pcSchedules[payload.originId] = scheduleMap;
  }),
  setPCSchedule: action((state, payload) => {
    state.pcSchedules = {
      ...state.pcSchedules,
      [payload.originId]: {
        ...(state.pcSchedules[payload.originId] || {}),
        [payload.schedule.scheduleEntryId]: payload.schedule,
      },
    };
  }),
  removePCSchedule: action((state, payload) => {
    const newSchedule = { ...state.pcSchedules[payload.originId] };
    delete newSchedule[payload.scheduleId];
    state.pcSchedules[payload.originId] = newSchedule;
  }),
  fetchPCSchedules: thunk(async (actions, originId, { injections }) => {
    try {
      const schedules = await injections.partnerConfigurationService.fetchPCSchedules(originId);
      actions.setPCSchedules({ originId: originId, schedules: schedules.results });
      return schedules;
    } catch (error) {
      actions.pcOriginFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured fetching schedules',
        asNotification: true,
      });
    }
  }),

  createPCSchedule: thunk(async (actions, params, { injections }) => {
    try {
      const schedule = await injections.partnerConfigurationService.createPCSchedule(params.originId, params.schedule);
      actions.setPCSchedule({ originId: params.originId, schedule: schedule });
      return schedule;
    } catch (error) {
      actions.pcOriginFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured creating new schedule.',
        asNotification: true,
      });
    }
  }),
  deletePCSchedule: thunk(async (actions, params, { injections }) => {
    try {
      const schedule = await injections.partnerConfigurationService.deletePCSchedule(
        params.originId,
        params.scheduleId,
      );
      actions.removePCSchedule({ originId: params.originId, scheduleId: schedule.scheduleEntryId });
      return schedule;
    } catch (error) {
      actions.pcOriginFailure({
        error,
        httpCode: parseStatusCode(error),
        message: 'An error occured deleting schedule.',
        asNotification: true,
      });
    }
  }),
};
